// src/components/admin/EmployeeEditor.jsx
import React, { useEffect, useMemo, useRef, useState } from 'react';
import * as usersApi from '../../services/usersApi';
import './EmployeeEditor.css';
import Constants from '../Constants/Constants';
import { useAuth } from '../../auth/AuthContext';

// ---------- utils ----------
const toBool = (v) => {
  const s = typeof v === 'string' ? v.trim().toLowerCase() : v;
  return !(s === 0 || s === '0' || s === false || s === 'false' || s == null);
};
const boolTo10 = (b) => (b ? '1' : '0');
const diff = (before, after) =>
  Object.fromEntries(
    Object.keys(after)
      .filter((k) => before[k] !== after[k])
      .map((k) => [k, after[k]])
  );
const confirmAction = (msg) =>
  typeof window !== 'undefined' && window.confirm ? window.confirm(msg) : true;

function resolveByWho() {
  try {
    if (typeof window !== 'undefined' && window.localStorage) {
      return (
        localStorage.getItem('loggedUser_Username') ||
        localStorage.getItem('loggedUser_Email') ||
        localStorage.getItem('username') ||
        localStorage.getItem('tm.username') ||
        'admin'
      );
    }
  } catch {}
  return 'admin';
}

// ---------- photo helpers (tenantServerUrl-based) ----------
/**
 * Build the canonical employee photo URL from tenantServerUrl + username.
 * e.g. tenantServerUrl = "https://wazzzasroom.tablemate.net/tablemate"
 * result = "https://wazzzasroom.tablemate.net/tablemate/admin/photos/<username>.jpg"
 */
const photoUrlFor = (tenantServerUrl, username) => {
  const u = String(username || '').trim();
  if (!u) return '';
  
  const base = String(tenantServerUrl || '').replace(/\/+$/, '');
  if (!base) {
    // soft fail – let onError handler show "No photo"
    console.warn(
      'EmployeeEditor: tenantServerUrl is not available; skipping photo URL for user:',
      u
    );
    return '';
  }

  return `${base}/tablemate/admin/photos/${encodeURIComponent(u)}.jpg`;
};

const withBust = (url) =>
  url ? `${url}${url.includes('?') ? '&' : '?'}v=${Date.now()}` : '';

// ---------- component ----------
const EmployeeEditor = ({
  employee,
  onCancel,
  onSaved,
  onChanged,
  onDeleted,
}) => {
  const { tenantServerUrl,user,selectedRestaurantId } = useAuth();
  const bywho = useMemo(() => resolveByWho(), []);
  // const [selectedRestaurantId, setRestaurantID] = useState(user.restaurants[0].id); 
  // constants (experience list etc.) tied to current tenant
  const constants = useMemo(
    () => new Constants(tenantServerUrl),
    [tenantServerUrl]
  );

  console.log("URL: "+tenantServerUrl)
   console.log("RestaurantID: "+selectedRestaurantId) //use first restaurant ID
    console.log("User: "+JSON.stringify(user))
  const EXPERIENCE_OPTIONS = constants.getExperienceList();

  const [busy, setBusy] = useState(null);

  // form state (mirror incoming employee)
  const [form, setForm] = useState(() => ({
    display_name: employee?.display_name ?? employee?.name ?? '',
    email: employee?.email ?? '',
    phone: employee?.phone ?? employee?.contact ?? '',
    username: employee?.username ?? '',
    role: employee?.role_id ?? '',

    gender: employee?.gender ?? '',
    nation: employee?.nation ?? employee?.nationality ?? '',
    postal_addr:
      employee?.postal_addr ??
      employee?.address ??
      employee?.post_address ??
      '',
    physical_addr:
      employee?.physical ?? employee?.physical_address ?? '',
    position: employee?.position ?? '',
    experience: employee?.experience ?? '',
    disabilities: employee?.disabilities ?? '',
    comments: employee?.comments ?? '',

    // 🔑 use DB field 'enabled' only
    enabled: boolTo10(toBool(employee?.enabled ?? 1)), // '1' or '0'
    photo_url: withBust(photoUrlFor(tenantServerUrl, employee?.username)),
  }));


  console.log("photo_url: "+form.photo_url)
  // roles dropdown (optional; fetched from API if available)
  const [roles, setRoles] = useState([]);
  const [rolesLoading, setRolesLoading] = useState(false);

  // file input ref for "Change photo"
  const fileRef = useRef(null);

  useEffect(() => {
    setForm((prev) => ({
      display_name: employee?.display_name ?? employee?.name ?? '',
      email: employee?.email ?? '',
      phone: employee?.phone ?? employee?.contact ?? '',
      username: employee?.username ?? '',
      role: employee?.role_id ?? '',

      gender: employee?.gender ?? '',
      nation: employee?.nation ?? employee?.nationality ?? '',
      postal_addr:
        employee?.postal_addr ??
        employee?.address ??
        employee?.post_address ??
        '',
      physical_addr:
        employee?.physical ?? employee?.physical_address ?? '',
      position: employee?.position ?? '',
      experience: employee?.experience ?? '',
      disabilities: employee?.disabilities ?? '',
      comments: employee?.comments ?? '',

      enabled: boolTo10(toBool(employee?.enabled ?? 1)),
      // refresh photo url if tenant or username changes
      photo_url:
        tenantServerUrl && employee?.username
          ? withBust(photoUrlFor(tenantServerUrl, employee?.username))
          : prev.photo_url || '',
    }));
    setBusy(null);
  }, [employee?.id, employee?.username, employee?.enabled, tenantServerUrl]);

  // roles fetch
  useEffect(() => {
    let live = true;
    (async () => {
      if (!usersApi.listRoles) return; // optional
      try {
        setRolesLoading(true);
        console.log("Fetching roles: "+tenantServerUrl)
        const raw = await usersApi.listRoles(tenantServerUrl);
        const opts = (raw || [])
          .map((r) => ({
            value: String(r?.value ?? r?.code ?? r?.id ?? r),
            label: String(r?.label ?? r?.name ?? r),
          }))
          .filter((o) => o.value);
        if (live) setRoles(opts);
      } finally {
        if (live) setRolesLoading(false);
      }
    })();
    return () => {
      live = false;
    };
  }, [selectedRestaurantId]);

  const roleOptions = useMemo(() => {
    const map = new Map(roles.map((o) => [o.value, o]));
    const cur = form.role || '';
    if (cur && !map.has(String(cur))) {
      map.set(String(cur), { value: String(cur), label: String(cur) });
    }
    return Array.from(map.values());
  }, [roles, form.role]);

  const setVal = (k, v) => setForm((prev) => ({ ...prev, [k]: v }));

  // 🔑 this drives the button label
  const isEnabled = form.enabled === '1';

  const titleLabel = useMemo(
    () =>
      `${employee?.display_name || employee?.username || employee?.email || 'Employee'}`,
    [employee]
  );

  // ---------- actions ----------
  const doSave = async (e) => {
    e.preventDefault();

    // before/after diff only for visible fields you edit commonly
    const payloadView = {
      display_name: form.display_name,
      email: form.email,
      phone: form.phone,
      role: form.role,
      experience: form.experience,
      postal_addr: form.postal_addr,
      enabled: form.enabled,
    };
    const before = {
      display_name: employee?.display_name ?? employee?.name ?? '',
      email: employee?.email ?? '',
      phone: employee?.phone ?? employee?.contact ?? '',
      role: employee?.role ?? employee?.role_id ?? '',
      experience: employee?.experience ?? '',
      postal_addr:
        employee?.postal_addr ??
        employee?.address ??
        employee?.post_address ??
        '',
      enabled: boolTo10(toBool(employee?.enabled ?? 1)),
    };
    const changes = diff(before, payloadView);
    if (!Object.keys(changes).length) {
      onCancel?.();
      return;
    }
    if (!confirmAction(`Save changes to ${titleLabel}?`)) return;

    const apiPayload = {
      id: String(employee.id),
      username: form.username,
      name: form.display_name, // map display_name -> name
      email: form.email,
      contact: form.phone, // map phone -> contact
      gender: form.gender || '',
      nation: form.nation || '',
      physical: form.physical_addr || form.postal_addr || '',
      post: form.postal_addr || '',
      position: form.position || '',
      experience: form.experience || '',
      disabilities: form.disabilities || '',
      comments: form.comments || '',
      role_id: form.role,
      bywho, // use resolved bywho
    };

    try {
      setBusy('save');
      await usersApi.editEmployee(tenantServerUrl,apiPayload);
      await onSaved?.();
    } finally {
      setBusy(null);
    }
  };

  // PASSWORD RESET POPUP
  const [pwOpen, setPwOpen] = useState(false);
  const [pw1, setPw1] = useState('');
  const [pw2, setPw2] = useState('');
  const [pwShow1, setPwShow1] = useState(false);
  const [pwShow2, setPwShow2] = useState(false);
  const [pwErr, setPwErr] = useState('');

  const doResetPw = async () => {
    setPw1('');
    setPw2('');
    setPwErr('');
    setPwShow1(false);
    setPwShow2(false);
    setPwOpen(true);
  };

  const submitNewPassword = async (e) => {
    e?.preventDefault?.();
    setPwErr('');

    if (!pw1 || !pw2) {
      setPwErr('Please enter the password twice.');
      return;
    }
    if (pw1 !== pw2) {
      setPwErr('Passwords do not match.');
      return;
    }
    if (pw1.length < 8) {
      setPwErr('Password must be at least 8 characters.');
      return;
    }

    try {
      setBusy('reset');

      const result = await usersApi.setEmployeePassword(tenantServerUrl,
        employee.id,
        employee.username,
        pw1,
        bywho
      );

      console.log('Login result ' + JSON.stringify(result));
      if (result.ok === true) {
        setPwOpen(false);
        await onChanged?.();
        alert('Password updated successfully.');
      } else {
        alert('Password update failed!');
      }
    } catch (err) {
      console.error('[EmployeeEditor] set password failed:', err);
      setPwErr(err?.message || 'Failed to update password.');
    } finally {
      setBusy(null);
    }
  };

  const doDisable = async () => {
    if (!confirmAction(`Deactivate ${titleLabel}'s account?`)) return;
    try {
      setBusy('disable');
      await usersApi.disableEmployee(tenantServerUrl,employee.id, bywho);
      setVal('enabled', '0');
      await onChanged?.();
    } finally {
      setBusy(null);
    }
  };

  const doEnable = async () => {
    if (!confirmAction(`Reactivate ${titleLabel}?`)) return;
    try {
      setBusy('enable');
      await usersApi.enableEmployee(tenantServerUrl,employee.id, bywho);
      setVal('enabled', '1');
      await onChanged?.();
    } finally {
      setBusy(null);
    }
  };

  const triggerFile = () => {
    if (!fileRef.current) {
      console.warn('[EmployeeEditor] triggerFile → fileRef is null');
      return;
    }
    fileRef.current.value = '';
    fileRef.current.click();
  };

  const onChangePhoto = async (e) => {
    console.log('[EmployeeEditor] onChangePhoto fired');
    const input = e.currentTarget;
    if (!input) {
      console.warn('[EmployeeEditor] onChangePhoto → no input element');
      return;
    }
    const file = input.files?.[0];
    console.log(
      '[EmployeeEditor] selected file:',
      file?.name,
      file?.size,
      file?.type
    );
    if (!file) return;

    if (!confirmAction(`Upload new profile picture for ${titleLabel}?`)) {
      input.value = '';
      return;
    }

    const allowed = [
      'image/jpeg',
      'image/png',
      'image/heic',
      'image/heif',
      'image/webp',
    ];
    if (file.type && !allowed.includes(file.type.toLowerCase())) {
      console.warn('[EmployeeEditor] unusual file type:', file.type);
    }

    if (!usersApi.updateProfilePhoto) {
      console.error(
        '[EmployeeEditor] usersApi.updateProfilePhoto is undefined'
      );
      alert('Upload function is not wired.');
      return;
    }
    if (!form.username) {
      console.warn(
        '[EmployeeEditor] username is empty — server will try to resolve via id'
      );
    }

    try {
      setBusy('photo');

      // 1) Instant local preview
      const localPreview = URL.createObjectURL(file);
      console.log('[EmployeeEditor] local preview URL:', localPreview);
      setVal('photo_url', localPreview);

      // 2) Upload (include username + debug for PHP)
      console.log('[EmployeeEditor] upload start', {
        id: employee.id,
        username: form.username,
        bywho,
        name: file.name,
        size: file.size,
        type: file.type,
      });

      const resp = await usersApi.updateProfilePhoto(tenantServerUrl,
        employee.id,
        file,
        bywho,
        { username: form.username, debug: true }
      );

      console.log('[EmployeeEditor] upload response:', resp);

      // 3) Switch to canonical URL (cache-busted) if tenantServerUrl available
      const canonical =
        tenantServerUrl && form.username
          ? withBust(photoUrlFor(tenantServerUrl, form.username))
          : '';
      console.log('[EmployeeEditor] switching to canonical:', canonical);
      setVal('photo_url', canonical);

      await onChanged?.();
    } catch (err) {
      console.error('[EmployeeEditor] photo upload failed:', err);
      alert(`Photo upload failed: ${err?.message || err}`);
      const fallback =
        tenantServerUrl && form.username
          ? withBust(photoUrlFor(tenantServerUrl, form.username))
          : '';
      setVal('photo_url', fallback);
    } finally {
      setBusy(null);
      input.value = '';
    }
  };

  const onDeletePhoto = async () => {
    if (!usersApi.deleteProfilePhoto) return;
    if (!confirmAction(`Delete profile photo for ${titleLabel}?`)) return;
    try {
      setBusy('delphoto');
      await usersApi.deleteProfilePhoto(tenantServerUrl,employee.id, bywho);
      const fallback =
        tenantServerUrl && form.username
          ? withBust(photoUrlFor(tenantServerUrl, form.username))
          : '';
      setVal('photo_url', fallback);
      await onChanged?.();
    } finally {
      setBusy(null);
    }
  };

  const doDeleteAccount = async () => {
    if (!confirmAction(`DELETE ${titleLabel}? This cannot be undone.`)) return;
    try {
      setBusy('delete');
      await usersApi.deleteEmployee(tenantServerUrl, employee.id, bywho, {
        username: form.username || employee.username,
        user_id: employee.user_id ?? employee.userId,
      });
      await onDeleted?.();
    } catch (err) {
      console.error('[EmployeeEditor] delete account failed:', err);
      alert(`Delete failed: ${err?.message || err}`);
    } finally {
      setBusy(null);
    }
  };

  // ---------- styles ----------
  const fieldLabel = { fontSize: 13, color: '#444', marginBottom: 4 };
  const inputStyle = {
    padding: '10px 12px',
    borderRadius: 8,
    border: '1px solid #ddd',
    background: '#fff',
    fontSize: 14,
  };

  const btn = {
    padding: '10px 14px',
    borderRadius: 8,
    border: '1px solid #dfe3e6',
    background: '#fff',
    cursor: 'pointer',
  };
  const btnPrimary = {
    ...btn,
    border: '1px solid #0a7',
    background: '#0c8',
    color: '#fff',
  };
  const btnDanger = {
    ...btn,
    border: '1px solid #f33',
    background: '#f55',
    color: '#fff',
  };

  // ---------- render ----------
  return (
    <div>
      <form onSubmit={doSave}>
        {/* Header */}
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontWeight: 700, fontSize: 18 }}>{titleLabel}</div>
          <div style={{ color: '#666', fontSize: 12 }}>
            {employee?.username || '—'} • {employee?.email || '—'}
          </div>
        </div>

        {/* Two columns: fields + photo panel */}
        <div
          className="employee-editor-split"
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr 320px',
            gap: 24,
          }}
        >
          {/* LEFT: Form fields */}
          <div
            id="layoutUserInformation"
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr',
              gap: 14,
            }}
          >
            {/* Name */}
            <div>
              <div style={fieldLabel}>Name: *</div>
              <input
                style={inputStyle}
                value={form.display_name}
                onChange={(e) => setVal('display_name', e.target.value)}
                placeholder="Full name"
                required
              />
            </div>

            {/* Email */}
            <div>
              <div style={fieldLabel}>Email: *</div>
              <input
                type="email"
                style={inputStyle}
                value={form.email}
                onChange={(e) => setVal('email', e.target.value)}
                placeholder="Email address"
                required
              />
            </div>

            {/* Contact */}
            <div>
              <div style={fieldLabel}>Contact:</div>
              <input
                style={inputStyle}
                value={form.phone}
                onChange={(e) => setVal('phone', e.target.value)}
                placeholder="Phone"
              />
            </div>

            {/* Username (read-only) */}
            <div>
              <div style={fieldLabel}>Username: *</div>
              <input
                style={{
                  ...inputStyle,
                  background: '#f7f7f7',
                  color: '#777',
                }}
                value={form.username}
                readOnly
              />
            </div>

            {/* Password reset */}
            <div>
              <div style={fieldLabel}>Password: *</div>
              <button
                type="button"
                onClick={doResetPw}
                disabled={busy === 'reset'}
                style={btn}
              >
                {busy === 'reset' ? 'SAVING…' : 'SET NEW PASSWORD'}
              </button>
            </div>

            {/* Role */}
            <div>
              <div style={fieldLabel}>Role: (Permissions) *</div>
              <select
                style={inputStyle}
                value={form.role || ''}
                onChange={(e) => setVal('role', e.target.value)}
                disabled={rolesLoading}
                required
              >
                <option value="">
                  {rolesLoading ? 'Loading…' : 'Select role'}
                </option>
                {roleOptions.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Experience */}
            <div>
              <div style={fieldLabel}>Experience:</div>
              <select
                style={inputStyle}
                value={form.experience || ''}
                onChange={(e) => setVal('experience', e.target.value)}
              >
                <option value="">Select…</option>
                {EXPERIENCE_OPTIONS.map((x) => (
                  <option key={x} value={x}>
                    {x}
                  </option>
                ))}
              </select>
            </div>

            {/* Postal Address */}
            <div>
              <div style={fieldLabel}>Postal Address:</div>
              <textarea
                rows={3}
                style={{ ...inputStyle, resize: 'vertical' }}
                value={form.postal_addr}
                onChange={(e) => setVal('postal_addr', e.target.value)}
                placeholder="Address"
              />
            </div>
          </div>

          {/* RIGHT: Photo panel */}
          <div>
            <div
              style={{
                width: '100%',
                aspectRatio: '1.4 / 1',
                borderRadius: 8,
                overflow: 'hidden',
                border: '1px solid #e6e6e6',
                background: '#fafafa',
                marginBottom: 6,
              }}
            >
              <img
                key={form.photo_url} // force re-render on cache-bust or local preview
                src={form.photo_url}
                alt="Profile"
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'contain',
                  display: 'block',
                  cursor: 'pointer',
                }}
                onClick={triggerFile}
                onError={(e) => {
                  // neutral placeholder if not found
                  e.currentTarget.onerror = null;
                  e.currentTarget.src =
                    'data:image/svg+xml;charset=utf-8,' +
                    encodeURIComponent(
                      `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 320 220">
                         <rect width="100%" height="100%" fill="#f3f4f6"/>
                         <text x="50%" y="50%" dominant-baseline="middle" text-anchor="middle"
                               font-family="Arial" font-size="14" fill="#9aa0a6">No photo</text>
                       </svg>`
                    );
                }}
              />
            </div>
            <div
              style={{ fontSize: 13, color: '#444', marginBottom: 10 }}
            >
              Profile Picture
            </div>

            <div
              className="employee-editor-photo-actions"
              style={{ display: 'flex', gap: 12, alignItems: 'center' }}
            >
              <button
                type="button"
                style={btn}
                onClick={triggerFile}
                disabled={busy === 'photo'}
              >
                {busy === 'photo' ? 'Uploading…' : 'Change photo'}
              </button>
              <button
                type="button"
                style={btn}
                onClick={onDeletePhoto}
                disabled={
                  busy === 'delphoto' || !usersApi.deleteProfilePhoto
                }
                title={
                  usersApi.deleteProfilePhoto
                    ? ''
                    : 'Delete endpoint not configured'
                }
              >
                {busy === 'delphoto' ? 'Deleting…' : 'Delete photo'}
              </button>
            </div>

            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              style={{ display: 'none' }}
              onClick={(e) => {
                e.currentTarget.value = '';
              }}
              onChange={onChangePhoto}
            />
          </div>
        </div>

        {/* Footer actions */}
        <div
          className="employee-editor-actions"
          style={{
            display: 'grid',
            gridTemplateColumns: '160px 1fr 200px 200px',
            gap: 12,
            marginTop: 18,
          }}
        >
          <button type="button" onClick={onCancel} style={btn}>
            CANCEL
          </button>

          <button
            type="button"
            onClick={doDeleteAccount}
            style={btnDanger}
            disabled={busy === 'delete'}
          >
            {busy === 'delete' ? 'DELETING…' : 'DELETE ACCOUNT'}
          </button>

          {isEnabled ? (
            <button
              type="button"
              onClick={doDisable}
              style={btn}
              disabled={busy === 'disable'}
            >
              {busy === 'disable'
                ? 'DEACTIVATING…'
                : 'DEACTIVATE ACCOUNT'}
            </button>
          ) : (
            <button
              type="button"
              onClick={doEnable}
              style={btn}
              disabled={busy === 'enable'}
            >
              {busy === 'enable'
                ? 'ACTIVATING…'
                : 'ACTIVATE ACCOUNT'}
            </button>
          )}

          <button
            type="submit"
            style={btnPrimary}
            disabled={busy === 'save'}
          >
            {busy === 'save' ? 'SAVING…' : 'SAVE CHANGES'}
          </button>
        </div>
      </form>

      {/* Password modal */}
      {pwOpen && (
        <div
          className="employee-editor-pw-modal-overlay"
          onClick={() => setPwOpen(false)}
        >
          <div
            className="employee-editor-pw-modal-content"
            onClick={(e) => e.stopPropagation()}
          >
            <div
              style={{
                fontWeight: 700,
                fontSize: 16,
                marginBottom: 10,
              }}
            >
              Set New Password — {titleLabel}
            </div>

            <form onSubmit={submitNewPassword}>
              <div style={{ marginBottom: 10 }}>
                <div
                  style={{
                    fontSize: 13,
                    color: '#444',
                    marginBottom: 4,
                  }}
                >
                  New password
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <input
                    type={pwShow1 ? 'text' : 'password'}
                    value={pw1}
                    onChange={(e) => setPw1(e.target.value)}
                    style={{ ...inputStyle, flex: 1 }}
                    autoFocus
                    placeholder="Enter new password"
                  />
                  <button
                    type="button"
                    style={btn}
                    onClick={() => setPwShow1((s) => !s)}
                    aria-label={
                      pwShow1 ? 'Hide password' : 'Show password'
                    }
                  >
                    {pwShow1 ? 'Hide' : 'Show'}
                  </button>
                </div>
              </div>

              <div style={{ marginBottom: 10 }}>
                <div
                  style={{
                    fontSize: 13,
                    color: '#444',
                    marginBottom: 4,
                  }}
                >
                  Confirm new password
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <input
                    type={pwShow2 ? 'text' : 'password'}
                    value={pw2}
                    onChange={(e) => setPw2(e.target.value)}
                    style={{ ...inputStyle, flex: 1 }}
                    placeholder="Re-enter new password"
                  />
                  <button
                    type="button"
                    style={btn}
                    onClick={() => setPwShow2((s) => !s)}
                    aria-label={
                      pwShow2 ? 'Hide password' : 'Show password'
                    }
                  >
                    {pwShow2 ? 'Hide' : 'Show'}
                  </button>
                </div>
              </div>

              {!!pwErr && (
                <div
                  style={{
                    color: '#b3261e',
                    fontSize: 12,
                    marginBottom: 8,
                  }}
                >
                  {pwErr}
                </div>
              )}

              <div
                style={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  gap: 8,
                }}
              >
                <button
                  type="button"
                  style={btn}
                  onClick={() => setPwOpen(false)}
                  disabled={busy === 'reset'}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  style={btnPrimary}
                  disabled={busy === 'reset'}
                >
                  {busy === 'reset' ? 'SAVING…' : 'Save Password'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmployeeEditor;
